<template>
  <div class="template-creator">
    <div :class="['overlay', {active: isLoading}]">
      <loading-spinner
        custom-id="template-creator-loader"
        :loading-status="isLoading"
      />
    </div>
    <div class="row mt-4 my-4">
      <div class="col-12">
        <TemplateBuilderHeader :title="templateId ? 'Edit Template' : 'Create Template'">
          <a
            v-if="form.id"
            id="SendToTestEmails"
            href=""
            @click="onSendTest"
          >
            <font-awesome-icon
              :icon="['far', 'paper-plane']"
              size="lg"
              class="mr-2"
            />Send Test
          </a>
          <span v-else>
            <font-awesome-icon
              :icon="['far', 'paper-plane']"
              size="lg"
              class="mr-2"
            />Send Test
          </span>
          <button
            id="btnCancel"
            class="btn btn-lg btn-outline-secondary px-4 white ml-3"
            @click="onCancel"
          >
            Cancel
          </button>
          <button
            id="btnSave"
            class="btn btn-lg btn-primary px-4 white ml-3"
            @click="getCustomCodeEditorHtml"
          >
            Save
          </button>
        </TemplateBuilderHeader>
      </div>
      <div class="col-12">
        <div class="divider mt-4" />
      </div>
    </div>
    <div v-if="alertKind === 'success'">
      <Alert
        v-model="alertKind"
        :icon="['far', 'info-circle']"
        :timeout="15"
      >
        Template saved successfully
      </Alert>
    </div>
    <div v-if="alertKind === 'danger'">
      <Alert
        v-model="alertKind"
        :icon="['far', 'info-circle']"
        :timeout="5"
      >
        An error has occurred while processing your request. Please contact <a href="mailto:producthelp@cendyn.com">producthelp@cendyn.com</a> for assistance.
      </Alert>
    </div>
    <div class="row mt-5">
      <div class="col-12">
        <form>
          <div class="form-row">
            <div :class="['form-group', 'col-md-12', 'required', {error: 'name' in errors}]">
              <ejs-textbox
                v-model="form.name"
                float-label-type="Always"
                placeholder="Name"
                :maxlength="nameMaxlength"
                :css-class="errors.name.length ? 'e-error' : ''"
                :blur="(args) => onFieldBlur('name', args)"
              />
              <div
                v-if="errors.name.length"
                class="error"
              >
                {{ errors.name }}
              </div>
            </div>
          </div>
          <div>
            <InputTags
              v-model="form.tags"
              label="Tags"
              :available-tags="availableTags"
              :set-error="(err) => {setError('tags', err)}"
            />
          </div>
          <div>
            <InputTags
              v-model="form.properties"
              label="Properties"
              :required="true"
              :available-tags="availableAccounts"
              :set-error="(err) => {setError('properties', err)}"
              :list-only="true"
              :parent-error="errors.properties"
              :min-tags="1"
            />
          </div>
          <div class="form-group">
            <ejs-textbox
              v-model="form.description"
              name="asd"
              float-label-type="Always"
              :multiline="true"
              placeholder="Description"
              :css-class="errors.description.length ? 'description e-error' : 'description'"
              :blur="(args) => onFieldBlur('description', args)"
            />
            <div
              v-if="errors.description.length"
              class="error"
            >
              {{ errors.description }}
            </div>
          </div>
          <div
            v-if="globalCompanyCheckbox && globalUserCheckbox"
            class="form-group"
          >
            <ejs-checkbox
              v-model="form.globalTemplate"
              css-class="checkboxColor"
              :checked="$route.query.isGlobal && globalUserCheckbox"
              label="Set as a global template."
            />
          </div>
        </form>
      </div>
    </div>
    <div class="row">
      <div class="col-12 text-center">
        <iframe
          id="customCodeEditorFrame"
          :src="iframeSource"
          class="custom-code-iframe-container w-100 h-100"
        />
      </div>
    </div>
    <BaseDialog
      ref="sendTestDialog"
      title="Send to Test Email(s)"
      width="50%"
      height="60%"
      :force-header="true"
      :custom-content-template="sendTest"
      :show-close-icon="true"
      :on-open="onSendTestDialogOpen"
      :on-close="onSendTestDialogClose"
      :enable-resize="true"
    />
    <BaseDialog
      ref="dynamicContentDialog"
      :is-iframe="false"
      :custom-content-template="dynamicContent"
      :title="dynamicContentDialogHeader"
      :force-header="true"
      :on-open="onMoreOptionsOpen"
      :on-close="onMoreOptionsClose"
      :show-close-icon="true"
      width="90%"
      height="90%"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import TemplateBuilderHeader from '@/components/TemplateBuilderHeader.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import BaseDialog from '@/components/Dialog/BaseDialog'
import InputTags from '@/components/InputTags'
import SendTest from '@/components/SendTest/index'
import Alert from '@/components/Alert'
import { iframeParentCommunication } from '@/mixins/iframeParentCommunication'
import DynamicContent from '@/components/DynamicContent'

export default {
  components: {
    TemplateBuilderHeader,
    LoadingSpinner,
    BaseDialog,
    InputTags,
    Alert
  },
  mixins: [iframeParentCommunication],
  data () {
    const templateId = this.$route.params.id
    return {
      alertKind: '',
      templateId,
      urlQuery: this.$helpers.getTemplateBuilderQuery(this.$route),
      availableTags: [],
      nameMaxlength: 255,
      descriptionMaxlength: 512,
      form: {
        id: 0,
        editorTypeId: 3,
        name: '',
        tags: [],
        properties: [],
        description: '',
        globalTemplate: false,
        templateContent: {
          by: '',
          html: '', // args[0]
          htmlJson: '', // args[0]
          styleJson: '' // args[0]
        },
        culture: 'en-US'
      },
      pristine: {
        name: true
      },
      errors: {
        name: '',
        tags: '',
        properties: '',
        description: ''
      },
      sendTest: SendTest,
      dynamicContent: DynamicContent,
      dynamicContentDialogHeader: 'Help'
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'getLoadingStatus',
      grapesJSConfig: 'getGrapesJSConfig',
      grapesJSInstance: 'getGrapesJSInstance',
      templateOnEdition: 'getTemplateOnEdition',
      availableAccounts: 'getAvailableAccounts',
      account: 'getAccount',
      globalCompanyCheckbox: 'getGlobalCompanyPermission',
      globalUserCheckbox: 'getGlobalUserPermission',
      activeType: 'getActiveType',
      eInsightLegacyURI: 'getEinsightUri'
    }),
    formName () {
      return this.form.name
    },
    formTemplateContent () {
      return this.form.templateContent
    },
    formDescription () {
      return this.form.description
    },
    formProperties () {
      return this.form.properties
    },
    hasError (field) {
      return field in this.errors && this.errors[field].length
    },
    hasErrors () {
      let nErrors = 0
      Object.keys(this.errors).forEach(key => {
        if (this.errors[key].length) nErrors++
      })
      return nErrors > 0
    },
    isEditingPageLoading () {
      return this.$store.getters.getLoadingStatusItem('template_creator_mounted')
    },
    iframeSource () {
      const decodedEmail = atob(this.$route.query.userEmail)
      return `${this.eInsightLegacyURI}/Project.mvc/Template/CustomCodeEditor?templateID=${this.form.id}&companyID=${this.$route.query.externalId}&userEmail=${decodedEmail}`
    }
  },
  watch: {
    account (newVal, oldVal) {
      if (newVal !== null && newVal !== oldVal) {
        if (this.templateId) {
          if (this.availableAccounts.length) {
            this.setProperties(this.availableAccounts)
          }
        }
      }
    },
    availableAccounts (newVal) {
      this.setProperties(newVal)
    },
    templateOnEdition (newVal, oldVal) {
      if (newVal && !oldVal) {
        const { tags, template, version } = this.templateOnEdition
        this.setProperties(this.availableAccounts)
        const templateContent = {
          by: 'server',
          html: version.html,
          htmlJson: version.htmlJson,
          styleJson: version.styleJson
        }
        this.form = {
          ...this.form,
          id: template.id,
          editorTypeId: template.editorTypeId,
          name: template.name,
          tags,
          description: template.description ?? '',
          globalTemplate: template.isGlobal,
          templateVersion: version.id,
          templateContent
        }
      }
    },
    formName (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.validateField('name')
      }
    },
    formDescription (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.validateField('description')
      }
    },
    formProperties (newVal, oldVal) {
      if (newVal.length !== oldVal.length) {
        this.validateField('properties')
      }
    },
    formTemplateContent (newVal, oldVal) {
      if (newVal.by === 'grapesjs') {
        this.submit()
      }
    }
  },
  created () {
    const loading = {
      name: 'template_creator_mounted',
      status: true
    }
    this.$store.commit('SET_LOADING', loading)
    this.checkCompanyGlobalPermissions()
    this.setUserGlobalPermission()
  },
  destroyed () {
    this.$store.commit('SET_DYNAMIC_CONTENT_MODAL_REF', null)
  },
  beforeDestroy () {
    window.removeEventListener('message', this.receivePostMessage)
  },
  mounted () {
    this.$store.commit('SET_DYNAMIC_CONTENT_MODAL_REF', this.$refs.dynamicContentDialog)
    window.addEventListener('message', this.receivePostMessage, false)
    if (this.account !== null) {
      this.form.properties.push(this.account)
    }
    if (this.$route.query.isGlobal) {
      this.form.globalTemplate = this.$route.query.isGlobal
    }

    const availablePropertiesPromise = this.getAvailableProperties(this.urlQuery)
    const popularTagsPromise = this.getPopularTags(this.urlQuery)
    const loading = {
      name: 'template_creator_mounted',
      status: true
    }
    this.$store.commit('SET_LOADING', loading)
    Promise.all([availablePropertiesPromise, popularTagsPromise]).then((response) => {
      const [availablePropertiesResponse, tagsResponse] = response
      if (availablePropertiesResponse.success) {
        this.availableProperties = availablePropertiesResponse.success.availableAccounts
      }

      if (tagsResponse.success) {
        this.availableTags = tagsResponse.success.tags
      }

      if (this.templateId && !this.templateOnEdition) {
        if (this.$route.query.isGlobal) {
          this.setPrebuiltTemplateOnEdition({ ...this.urlQuery, templateId: this.templateId })
        } else {
          this.setTemplateOnEdition({ ...this.urlQuery, templateId: this.templateId })
        }
      }

      loading.status = false
      this.$store.commit('SET_LOADING', loading)
    }).catch(err => {
      console.error(err)
    })
  },
  methods: {
    ...mapActions([
      'createTemplate',
      'setTemplateOnEdition',
      'setPrebuiltTemplateOnEdition',
      'updateTemplateOnEdition',
      'updatePrebuiltTemplateOnEdition',
      'getAvailableProperties',
      'getPopularTags',
      'uncheckSeedList',
      'getSeedList'
    ]),
    setProperties (avProperties) {
      if (this.templateOnEdition) {
        const { accountIds } = this.templateOnEdition
        if (avProperties.length && accountIds && this.account) {
          const properties = []
          for (const key in avProperties) {
            if (accountIds.includes(avProperties[key].id)) {
              properties.push({ ...avProperties[key] })
            }
          }
          this.form.properties = properties
        }
      }
    },
    checkCompanyGlobalPermissions () {
      const externalId = this.$route.query.externalId
      const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
      this.$store.dispatch('setCompanyGlobalPermission', { externalId, userEmail, showLoading: true })
    },
    setUserGlobalPermission () {
      const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
      this.$store.dispatch('setUserGlobalPermission', { userEmail, showLoading: true })
    },
    setError (field, error) {
      this.errors[field] = error ?? ''
    },
    onFieldBlur (field, args) {
      if (this.errors[field].length === 0) {
        this.validateField(field)
      }
    },
    onSendTest (e) {
      e.preventDefault()
      this.$store.commit('SET_EMAIL_TESTING', {
        sendTestModalRef: this.$refs.sendTestDialog
      })

      const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : ''
      this.getSeedList({ userEmail })

      this.$refs.sendTestDialog.openDialog()
    },
    onSendTestDialogOpen () {
      this.$store.commit('SET_EMAIL_TESTING_HAS_DIALOG_CLOSE', false)
      this.$store.commit('SET_SEND_TEST_MODAL_IS_OPEN', true)
    },
    onSendTestDialogClose () {
      this.$store.commit('SET_EMAIL_TESTING_HAS_DIALOG_CLOSE', true)
      this.$store.commit('SET_SEND_TEST_MODAL_IS_OPEN', false)
    },
    validateField (field = '') {
      if (!(field in this.form) || typeof this.form[field] === 'undefined') return

      switch (field) {
        case 'name': {
          const { name } = this.form
          this.errors[field] = this.$validation.isEmpty(name, 'Title field is required') || this.$validation.minLength(name, 3, 'The name should have at least [MIN] characters') || this.$validation.maxLength(name, this.nameMaxLength, 'The name should have at most [MAX] characters')
        }
          break
        case 'description': {
          const { description } = this.form
          if (description.length) {
            this.errors[field] = this.$validation.maxLength(description, this.descriptionMaxlength, 'The description should have at most [MAX] characters')
          }
        }
          break
        case 'properties': {
          const { properties } = this.form
          if (properties.length === 0) {
            this.errors[field] = this.$validation.isEmpty(properties, 'Properties field is required')
          }
        }
          break
      }
    },
    validateForm () {
      this.validateField('name')
      this.validateField('description')
      this.validateField('properties')
    },
    onCancel () {
      this.$store.commit('SET_TEMPLATE_ON_EDITION', null)
      const query = Object.assign({}, this.$route.query)
      delete query.isGlobal
      this.$router.push({ name: 'TemplateBuilder', query })
    },
    submit () {
      let response
      this.validateForm()
      if (!this.hasErrors) {
        const data = {
          name: this.form.name,
          description: this.form.description,
          // templateVersion: this.form.templateVersion,
          htmlJson: this.form.templateContent.htmlJson,
          styleJson: this.form.templateContent.styleJson,
          html: this.form.templateContent.html,
          culture: this.form.culture,
          tags: this.form.tags.map(tag => tag.name),
          accountIds: this.form.properties.map(p => p.id),
          isGlobal: this.form.globalTemplate
        }
        if (this.form.id) {
          data.id = this.form.id
          data.editorTypeId = this.form.editorTypeId
          if (!this.form.globalTemplate) {
            response = this.updateTemplateOnEdition({ ...this.urlQuery, data })
            response
              .then(({ success = null, error = null }) => {
                if (success) {
                  this.form.templateVersion = success.templateVersionId
                  this.alertKind = 'success'
                } else if (error) {
                  console.log(error)
                  this.alertKind = 'danger'
                }
              })
              .catch(error => {
                this.alertKind = 'danger'
                console.log(error)
              })
          } else {
            response = this.updatePrebuiltTemplateOnEdition({ ...this.urlQuery, data })
            response
              .then(({ success = null, error = null }) => {
                if (success) {
                  this.form.templateVersion = success.templateVersionId
                  this.alertKind = 'success'
                } else if (error) {
                  console.log(error)
                  this.alertKind = 'danger'
                }
              })
              .catch(error => {
                this.alertKind = 'danger'
                console.log(error)
              })
          }
        } else {
          data.editorTypeId = this.form.editorTypeId
          response = this.createTemplate({ ...this.urlQuery, data })
          response
            .then(({ success, error }) => {
              if (success) {
                this.templateId = success.templateId
                this.form.id = success.templateId
                this.form.templateVersion = success.templateVersionId

                // Updating the query templateId to allow send test action to work
                this.$route.query.templateId = success.templateId
                this.$router.push({ name: 'TemplateCreatorCustomCode', params: { id: this.templateId }, query: this.$route.query }).catch(error => {
                  if (error.name !== 'NavigationDuplicated') {
                    console.log('[ERROR]', error)
                  }
                })
                this.alertKind = 'success'

                this.setTemplateOnEdition({ ...this.urlQuery, templateId: this.templateId })
              } else if (error) {
                console.log(error)
                this.alertKind = 'danger'
              }
            })
            .catch(error => {
              this.alertKind = 'danger'
              console.log(error)
            })
        }
      }
    },
    getCustomCodeEditorHtml () {
      const frame = document.getElementById('customCodeEditorFrame')
      const obj = { action: 'getEditorHtml' }
      frame.contentWindow.postMessage(obj, '*')
      this.$store.commit('SET_LOADING', true)
    },
    onMoreOptionsOpen () {
      this.$store.commit('SET_DYNAMIC_CONTENT_MODAL_OPEN', true)
    },
    onMoreOptionsClose () {
      this.$store.commit('SET_DYNAMIC_CONTENT_MODAL_OPEN', false)
    },
    receivePostMessage (e) {
      const { data } = e
      if (data) {
        switch (data.action) {
          case 'openMoreOption':
            this.$store.commit('SET_DYNAMIC_CONTENT_SELECTED_TAB', 3)
            this.$refs.dynamicContentDialog.openDialog()
            break
          case 'getEditorHtml':
            if (data.args) {
              this.form.templateContent.html = data.args[0]
              this.$store.commit('SET_LOADING', false)
              this.submit()
            }
            break
          default:
            break
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '../../node_modules/@syncfusion/ej2-base/styles/bootstrap4.css';
  @import '../../node_modules/@syncfusion/ej2-inputs/styles/bootstrap4.css';
  @import '../../node_modules/@syncfusion/ej2-buttons/styles/bootstrap4.css';

  .template-creator {
    position: relative;
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
    display: none;
    background-color: #ffffffcf;
    &.active {
      display: block;
    }
  }

  a#SendToTestEmails {
    &:hover {
      text-decoration: none;
    }
  }

  .divider {
    border-bottom: 1px solid $gray-light;
  }

  .e-float-input input, .e-float-input.e-control-wrapper input, .e-float-input textarea, .e-float-input.e-control-wrapper textarea {
    border-radius: 2px;
  }
  .e-btn-primary {
    border-radius: 2px;
    color: $white;
    background-color: $bocalipstick;
    border-color: $bocalipstick-dark;
  }
  .description textarea {
    height: 100px;
  }

  .custom-code-iframe-container {
    min-height: 952px;
  }
</style>
